import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { msalInstance } from './config/authConfig';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RequireRole from './components/auth/RequireRole';
import UserProfileSync from './components/auth/UserProfileSync';
import Welcome from './components/auth/Welcome';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import GlobalLoadingOverlay from './components/common/GlobalLoadingOverlay';
import CustomerSelector from './components/customers/CustomerSelector';
import PhaseDashboard from './components/phases/PhaseDashboard';
import PhaseManager from './components/phases/PhaseManager';
import CredentialForm from './components/credentials/CredentialForm';
import MetadataViewer from './components/metadata/MetadataViewer';
import FileUploader from './components/files/FileUploader';
import MappingCanvasV3 from './components/mapping/MappingCanvasV3';
import PhaseEntities from './components/phases/PhaseEntities';
import ValidationPanel from './components/validation/ValidationPanel';
import ExportConfiguration from './components/export/ExportConfiguration';
import ProgressMonitor from './components/progress/ProgressMonitor';
import Reports from './components/reports/Reports';
import Settings from './components/settings/Settings';
import UserManagement from './components/admin/UserManagement';
import WizardPage from './components/wizard/wizard-page';
import SharePointAccessDenied from "./components/error/SharePointAccessDenied";
import ErrorBoundary from './components/error/ErrorBoundary';


import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <DndProvider backend={HTML5Backend}>
            <Router>
              <div className="app">
                <GlobalLoadingOverlay />
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <UserProfileSync>
                          <Layout>
                            <Routes>
                              <Route path="/dashboard" element={<Dashboard />} />
                              <Route path="/sharepoint/access-denied" element={<SharePointAccessDenied />} />
                              <Route
                                path="/customers"
                                element={
                                  <RequireRole role={['admin', 'consultant']}>
                                    <CustomerSelector />
                                  </RequireRole>
                                }
                              />
                              <Route path="/customers/:customerId/phases" element={<PhaseManager />} />
                              <Route path="/customers/:customerId/phases/:phaseId" element={<PhaseDashboard />} />
                              <Route path="/customers/:customerId/phases/:phaseId/credentials" element={<CredentialForm />} />
                              <Route path="/customers/:customerId/phases/:phaseId/metadata" element={<MetadataViewer />} />
                              <Route path="/customers/:customerId/phases/:phaseId/entities" element={<PhaseEntities />} />
                              <Route path="/customers/:customerId/phases/:phaseId/files" element={<FileUploader />} />
                              <Route path="/customers/:customerId/phases/:phaseId/mapping-v3" element={<MappingCanvasV3 />} />
                              <Route path="/customers/:customerId/phases/:phaseId/validation" element={<ValidationPanel />} />
                              <Route path="/customers/:customerId/phases/:phaseId/export" element={<ExportConfiguration />} />
                              <Route path="/customers/:customerId/phases/:phaseId/progress" element={<ProgressMonitor />} />
                              <Route path="/customers/:customerId/phases/:phaseId/wizard" element={<WizardPage />} />
                              <Route path="/reports" element={<Reports />} />
                              <Route path="/settings" element={<Settings />} />
                              <Route
                                path="/admin/users"
                                element={
                                  <RequireRole role="admin">
                                    <UserManagement />
                                  </RequireRole>
                                }
                              />
                              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </Layout>
                        </UserProfileSync>
                      </ProtectedRoute>
                    }
                  />
                </Routes>

                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      style: {
                        background: '#10B981',
                      },
                    },
                    error: {
                      style: {
                        background: '#EF4444',
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </DndProvider>
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
};
export default App;